import { useEffect, useRef } from 'react';

/**
 * Keeps the participant's screen awake during a live session so they
 * don't miss activity launches when the phone auto-locks.
 *
 * Uses the browser Screen Wake Lock API (iOS Safari 16.4+, Android Chrome,
 * desktop Chrome/Edge/Firefox). Silently no-ops on unsupported browsers.
 *
 * Re-acquisition strategy (3 layers):
 *   1. visibilitychange  — re-acquire when tab becomes visible again (after
 *      backgrounding on iOS, switching apps, etc.)
 *   2. release event     — re-acquire immediately if the OS drops the lock
 *      while the tab is still visible (iOS idle timeout, memory pressure).
 *   3. periodic check    — every 30 seconds, verify the lock is still held
 *      and re-acquire if lost. Catches edge cases where neither the release
 *      event nor visibilitychange fires reliably (observed on some iOS/
 *      Android WebView builds).
 *
 * @param {boolean} enabled - when true, actively hold the wake lock.
 */
const useWakeLock = (enabled = true) => {
  const lockRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
      return undefined;
    }

    let cancelled = false;
    let acquireInFlight = false;

    const acquire = async () => {
      if (cancelled || lockRef.current || acquireInFlight) return;
      acquireInFlight = true;
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          lock.release().catch(() => {});
          acquireInFlight = false;
          return;
        }
        lockRef.current = lock;

        lock.addEventListener('release', () => {
          if (lockRef.current === lock) lockRef.current = null;
          // Re-acquire immediately if the tab is still visible. Without this,
          // the lock stays dropped until the next visibilitychange — which may
          // never come if the user is simply idle on the page.
          if (!cancelled && document.visibilityState === 'visible') {
            setTimeout(() => acquire(), 1000);
          }
        });

        console.log('[WakeLock] screen wake lock acquired');
      } catch (err) {
        console.warn('[WakeLock] request failed:', err?.message || err);
      } finally {
        acquireInFlight = false;
      }
    };

    // Layer 1: re-acquire on visibility change (tab foregrounded)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !lockRef.current) {
        acquire();
      }
    };

    // Layer 3: periodic safety net — re-acquire if somehow lost
    const intervalId = setInterval(() => {
      if (!cancelled && !lockRef.current && document.visibilityState === 'visible') {
        console.log('[WakeLock] periodic check — lock lost, re-acquiring');
        acquire();
      }
    }, 30000);

    acquire();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (lockRef.current) {
        lockRef.current.release().catch(() => {});
        lockRef.current = null;
      }
    };
  }, [enabled]);
};

export default useWakeLock;
